import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Eraser, Undo2, Trash2, Pencil } from "lucide-react";

const COLORS = ["#2C4A3E", "#C87D53", "#E05A47", "#F2B33D", "#6B9080", "#3B6EA8", "#8E5BA8", "#2C2C2C"];
const SIZES = [3, 7, 14];
const W = 900, H = 560;

export const DrawPad = forwardRef(({ onChange }, ref) => {
  const cv = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [color, setColor] = useState(COLORS[0]);
  const [size, setSize] = useState(SIZES[1]);
  const [erase, setErase] = useState(false);
  const [history, setHistory] = useState([]);
  const strokes = useRef(0);
  const [count, setCount] = useState(0);
  const ctx = () => cv.current.getContext("2d");
  const blank = () => { const c = ctx(); c.fillStyle = "#FFFFFF"; c.fillRect(0, 0, W, H); };
  useEffect(() => { blank(); }, []);
  useImperativeHandle(ref, () => ({
    isEmpty: () => strokes.current === 0,
    toBlob: () => new Promise((res) => cv.current.toBlob(res, "image/png")),
  }), [history]);
  const pos = (e) => { const r = cv.current.getBoundingClientRect(); return [((e.clientX - r.left) * W) / r.width, ((e.clientY - r.top) * H) / r.height]; };
  const line = (a, b, pressure) => {
    const c = ctx();
    c.strokeStyle = erase ? "#FFFFFF" : color; c.lineWidth = (erase ? size * 2.5 : size) * (pressure || 1); c.lineCap = "round"; c.lineJoin = "round";
    c.beginPath(); c.moveTo(...a); c.lineTo(...b); c.stroke();
  };
  const down = (e) => {
    e.preventDefault(); cv.current.setPointerCapture(e.pointerId);
    const snap = ctx().getImageData(0, 0, W, H);
    setHistory((h) => [...h.slice(-19), snap]);
    strokes.current += 1; setCount(strokes.current);
    drawing.current = true; last.current = pos(e); line(last.current, last.current, e.pressure > 0 && e.pointerType === "pen" ? e.pressure * 1.6 : 1);
    onChange?.(true);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const p = pos(e); line(last.current, p, e.pressure > 0 && e.pointerType === "pen" ? e.pressure * 1.6 : 1); last.current = p;
  };
  const up = () => { drawing.current = false; };
  const undo = () => {
    if (!history.length) return;
    ctx().putImageData(history[history.length - 1], 0, 0);
    setHistory(history.slice(0, -1));
    strokes.current = Math.max(0, strokes.current - 1); setCount(strokes.current); onChange?.(strokes.current > 0);
  };
  const clear = () => { blank(); setHistory([]); strokes.current = 0; setCount(0); onChange?.(false); };
  const tool = (active) => `p-2.5 rounded-full border transition-colors ${active ? "bg-[#2C4A3E] text-white border-[#2C4A3E]" : "bg-white text-[#2C4A3E] border-[#E6DEC3] hover:bg-[#F1EADB]"}`;
  return (
    <div data-testid="draw-pad">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <button type="button" onClick={() => setErase(false)} className={tool(!erase)} aria-label="Pen" data-testid="draw-tool-pen"><Pencil className="w-4 h-4" /></button>
        <button type="button" onClick={() => setErase(true)} className={tool(erase)} aria-label="Eraser" data-testid="draw-tool-eraser"><Eraser className="w-4 h-4" /></button>
        <span className="w-px h-6 bg-[#E6DEC3] mx-1" />
        {COLORS.map((c) => <button type="button" key={c} onClick={() => { setColor(c); setErase(false); }} aria-label={`Color ${c}`} data-testid={`draw-color-${c.slice(1)}`}
          className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${color === c && !erase ? "ring-2 ring-offset-2 ring-[#2C4A3E]" : ""}`} style={{ background: c }} />)}
        <span className="w-px h-6 bg-[#E6DEC3] mx-1" />
        {SIZES.map((s) => <button type="button" key={s} onClick={() => setSize(s)} aria-label={`Brush size ${s}`} data-testid={`draw-size-${s}`} className={`${tool(size === s)} w-10 h-10 grid place-items-center`}>
          <span className="rounded-full bg-current" style={{ width: s + 2, height: s + 2 }} /></button>)}
        <span className="ml-auto flex gap-2">
          <button type="button" onClick={undo} disabled={!history.length} className={`${tool(false)} disabled:opacity-40`} aria-label="Undo" data-testid="draw-undo"><Undo2 className="w-4 h-4" /></button>
          <button type="button" onClick={clear} disabled={!count} className={`${tool(false)} disabled:opacity-40`} aria-label="Clear" data-testid="draw-clear"><Trash2 className="w-4 h-4" /></button>
        </span>
      </div>
      <canvas ref={cv} width={W} height={H} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onPointerLeave={up}
        className="w-full rounded-3xl border border-[#E6DEC3] bg-white touch-none cursor-crosshair shadow-inner" style={{ aspectRatio: `${W} / ${H}` }} data-testid="draw-canvas" />
      <p className="text-xs text-stone-500 mt-2">Draw or write with your finger, a stylus or the mouse.</p>
    </div>
  );
});
DrawPad.displayName = "DrawPad";
